import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Grid, 
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import UsagePatternCard from './UsagePatternCard';
import SalesAnalysisCard from './SalesAnalysisCard';
import CustomerStatusCard from './CustomerStatusCard';
import RecommendationCard from './RecommendationCard';
import AIProfileSummaryCard from './AIProfileSummaryCard';
import { CustomerInsightService, CustomerInsight } from '../../services/customerInsightService';

interface CustomerInsightsProps {
  customerId: number;
}

const CustomerInsights: React.FC<CustomerInsightsProps> = ({ customerId }) => {
  const [insights, setInsights] = useState<CustomerInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const loadInsights = async () => {
      // デバウンス処理で無限ループを防止
      if (!customerId || customerId <= 0) {
        console.warn('CustomerInsights: Invalid customerId:', customerId);
        setError('顧客IDが無効です。');
        setInsights(null);
        setLoading(false);
        return;
      }

      try {
        console.log('CustomerInsights: Loading insights for customer:', customerId);
        setLoading(true);
        setError(null);
        
        // 非同期処理化して確実にsetLoading(false)を実行
        await new Promise(resolve => setTimeout(resolve, 0));
        
        // CustomerInsightServiceを使用してデータを取得
        const data = await CustomerInsightService.calculateInsights(customerId);
        
        console.log('CustomerInsights: Calculation result:', data ? 'Success' : 'No data');
        
        if (!data) {
          setError('施術記録が見つかりません。施術記録を登録してから分析をご確認ください。');
          setInsights(null);
        } else {
          setInsights(data);
          setError(null);
        }
      } catch (err) {
        console.error('CustomerInsights: Failed to load insights:', err);
        setError('データの読み込みに失敗しました。');
        setInsights(null);
      } finally {
        console.log('CustomerInsights: Loading completed for customer:', customerId);
        setLoading(false);
      }
    };

    loadInsights();
  }, [customerId]);

  if (error && !loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          {error}
        </Alert>
      </Box>
    );
  }

  const gridColumns = isMobile ? 12 : isTablet ? 6 : 6;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {/* AI顧客分析カード - 新規追加 */}
        <Grid item xs={12}>
          <AIProfileSummaryCard customerId={customerId} />
        </Grid>
        
        {/* 利用傾向分析 */}
        <Grid item xs={12} sm={gridColumns} lg={6}>
          <UsagePatternCard 
            data={insights?.usagePattern || null} 
            loading={loading}
            treatmentRecords={insights?.treatmentRecords || []}
          />
        </Grid>

        {/* 売上・LTV分析 */}
        <Grid item xs={12} sm={gridColumns} lg={6}>
          <SalesAnalysisCard 
            data={insights?.salesAnalysis || null}
            loading={loading}
            treatmentRecords={insights?.treatmentRecords || []}
          />
        </Grid>

        {/* 顧客ステータス */}
        <Grid item xs={12} sm={gridColumns} lg={6}>
          <CustomerStatusCard 
            data={insights?.customerStatus || null}
            loading={loading}
            treatmentRecords={insights?.treatmentRecords || []}
          />
        </Grid>

        {/* レコメンド・アクション */}
        <Grid item xs={12} sm={gridColumns} lg={6}>
          <RecommendationCard 
            data={insights?.recommendations || null}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerInsights;